import { X } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';
import { cn } from './cn';

interface Toast { id: number; title: string; body?: string; tone?: 'neutral' | 'warning' }
const ToastContext = createContext<(toast: Omit<Toast, 'id'>) => void>(() => {});
export const useToast = () => useContext(ToastContext);

let nextId = 1;
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setToasts(list => list.filter(t => t.id !== id)), []);
  const push = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = nextId++;
    setToasts(list => [...list.slice(-2), { ...toast, id }]);
    setTimeout(() => dismiss(id), 6000);
  }, [dismiss]);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4 sm:items-end sm:pr-6">
        {toasts.map(t => (
          <div key={t.id} role="status" className={cn('pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border bg-raised px-4 py-3 shadow-pop animate-rise',
            t.tone === 'warning' ? 'border-attention/40' : 'border-line')}>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-ink">{t.title}</p>
              {t.body && <p className="mt-0.5 text-[13px] text-ink-2">{t.body}</p>}
            </div>
            <button type="button" aria-label="Dismiss" onClick={() => dismiss(t.id)} className="rounded p-0.5 text-ink-3 hover:text-ink"><X className="size-4" /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
